/**
 * Tipos e rótulos do cadastro de máquina em etapas (issue #362).
 */
import {
  MachineAdequacyPriority,
  MachineAdequacyProgress,
  MachineProtectionState,
  MachineProtectionType,
  MachineStatus,
} from '../../../../types/machine';
import type { SourceGenerator } from '../../../../types/sourceGenerator';

export type MachineWizardStepKey = 'identification' | 'protections' | 'adequacies' | 'appraisal';

export const MACHINE_WIZARD_STEPS: ReadonlyArray<{ key: MachineWizardStepKey; label: string }> = [
  { key: 'identification', label: 'Identificação' },
  { key: 'protections', label: 'Proteções instaladas' },
  { key: 'adequacies', label: 'Adequações necessárias' },
  { key: 'appraisal', label: 'Apreciação de risco' },
];

/** O que uma etapa de lista (proteções, adequações) oferece a quem a controla. */
export interface StepHandle {
  /** Guarda o formulário aberto, se tiver algo digitado. `false` quando há erro a corrigir. */
  salvarPendente: () => Promise<boolean>;
}

export interface ListStepProps {
  machineId: string;
  /** Quem pode alterar (`machine.update`); sem isso a etapa só mostra. */
  canEdit: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onSaved: (message: string) => void;
}

export interface IdentificationValues {
  /** Empresa dona da máquina; só é perguntada na criação, quando não há empresa ativa. */
  companyId: string;
  name: string;
  tag: string;
  departmentId: string;
  sourceGenerator: SourceGenerator | null;
  manufacturer: string;
  model: string;
  serialNumber: string;
  yearOfManufacture: string;
  location: string;
  status: MachineStatus;
  observations: string;
  maintenanceFrequency: string;
  lastMaintenanceDate: string;
  nextMaintenanceDate: string;
}

export type IdentificationErrors = Partial<Record<keyof IdentificationValues, string>>;

export const EMPTY_IDENTIFICATION: IdentificationValues = {
  companyId: '',
  name: '',
  tag: '',
  departmentId: '',
  sourceGenerator: null,
  manufacturer: '',
  model: '',
  serialNumber: '',
  yearOfManufacture: '',
  location: '',
  status: MachineStatus.ACTIVE,
  observations: '',
  maintenanceFrequency: '',
  lastMaintenanceDate: '',
  nextMaintenanceDate: '',
};

export const MACHINE_STATUS_LABELS: Record<MachineStatus, string> = {
  [MachineStatus.ACTIVE]: 'Em operação',
  [MachineStatus.INACTIVE]: 'Parada',
  [MachineStatus.MAINTENANCE]: 'Em manutenção',
  [MachineStatus.DECOMMISSIONED]: 'Desativada de vez',
};

export const PROTECTION_TYPE_LABELS: Record<MachineProtectionType, string> = {
  PROTECAO_FIXA: 'Proteção fixa',
  PROTECAO_MOVEL_INTERTRAVADA: 'Proteção móvel com intertravamento',
  CORTINA_DE_LUZ: 'Cortina de luz',
  COMANDO_BIMANUAL: 'Comando bimanual',
  TAPETE_SENSIVEL: 'Tapete sensível',
  PARADA_DE_EMERGENCIA: 'Parada de emergência',
  OUTRA: 'Outra (descrever)',
};

export const PROTECTION_STATE_LABELS: Record<MachineProtectionState, string> = {
  CONFORME: 'Conforme',
  EM_DESACORDO: 'Em desacordo',
  DANIFICADA: 'Danificada',
};

export const PRIORITY_LABELS: Record<MachineAdequacyPriority, string> = {
  ALTA: 'Alta',
  MEDIA: 'Média',
  BAIXA: 'Baixa',
};

export const PROGRESS_LABELS: Record<MachineAdequacyProgress, string> = {
  NAO_INICIADA: 'Não iniciada',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDA: 'Concluída',
};
